import React from 'react';
import { makeStyles } from '@material-ui/core';
import { mapProps } from 'recompose';
import { graphql, compose, withApollo } from 'react-apollo';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router';
// import Attachment from '@mui/icons-material/Attachment';

import { FIRST_LAST } from 'selectors/formatName/selector';
import UserBadge from 'components/threepp/UserBadge/UserBadge';
import TextArea from 'components/common/TextArea/TextArea';
import Button from 'components/common/Buttons/Button';
import { useViewport } from 'utils/getViewport/getViewport';
// import FileSelector from 'components/common/FileSelector/FileSelector';
// import Attachments from './Attachments/Attachments';
import { getCurrentUser, saveComment } from 'graphql/operations';
import { isEmptyString } from 'utils/functions';
import { showErrorMessage, showSuccessMessage } from 'components/common/Alert/Alert';
import { createTextareaValidationFn } from 'utils/validations/validations';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: '1.25rem',
    [theme.breakpoints.down(theme.breakpoints.values.mobile)]: {
      paddingTop: theme.whitespace.base
    },
    '&.editMode': {
      paddingTop: theme.whitespace.base
    }
  },
  input: {
    flexGrow: '1'
  },
  inputField: {
    marginLeft: '2rem'
  },
  inputActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: '1rem 0'
  },
  attachAction: {
    display: 'flex',
    color: theme.palette.icon.iconColor,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main
    }
  },
  attachmentIcon: {
    marginRight: theme.whitespace.least
  },
  actionRow: {
    textAlign: 'right',
    [theme.breakpoints.down(theme.breakpoints.values.mobile)]: {
      display: 'flex',
      justifyContent: 'space-between'
    }
  },
  cancelBtn: {
    marginRight: theme.whitespace.base
  }
}));

export const CommentInputBase = props => {
  const {
    translate,
    editMode,
    getCurrentUser: currentUser,
    comment,
    onCancel,
    sourceFieldId,
    commentMetadata,
    apolloClient,
    updateCommentsState,
    isAssessmentReadOnly
  } = props;

  const [buttonDisabledWhenSave, setButtonDisabledWhenSave] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(comment?.comment_text);
  // const [attachments, setAttachments] = useState([]);
  const { isMobile } = useViewport();
  const classes = useStyles();
  const badgeStyle = { height: '30px', width: '30px' };
  const inputValidation = createTextareaValidationFn(commentMetadata?.text_max_characters_allowed);

  React.useEffect(() => {
    comment?.comment_text && setInputValue(comment.comment_text);
  }, [comment]);

  // useEffect(() => {
  //   editMode && comment?.attachments && setAttachments(comment?.attachments);
  // }, [editMode, comment]);

  const onCancelHandler = () => onCancel();

  const isSaveDisabled = React.useCallback(() => {
    const maxCharactersErrorObj = inputValue && commentMetadata?.text_max_characters_allowed ? inputValidation(inputValue) : null;
    return isEmptyString(inputValue) || buttonDisabledWhenSave || maxCharactersErrorObj !== null;
  }, [inputValue, buttonDisabledWhenSave, commentMetadata, inputValidation]);

  const saveCommentFunc = async comment_id => {
    setButtonDisabledWhenSave(true);
    const commentId = comment_id || null;
    const value = inputValue;
    const assessmentId = commentMetadata?.assessment_id;

    try {
      const { data } = await apolloClient.mutate({
        mutation: saveComment,
        variables: {
          commentId,
          sourceFieldId,
          value,
          assessmentId
        }
      });
      if (!data.saveComment.comment) {
        showErrorMessage(translate('comments.saveComment.maxCharacterError'), {
          autoHideDuration: 5000
        });
      } else {
        setInputValue('');
        updateCommentsState(data.saveComment.comment);
        if (comment_id) {
          showSuccessMessage(translate('comments.editComment.successMsg'));
          onCancelHandler();
        }
      }
    } catch {
      const errorMsg = comment_id ? translate('comments.editComment.errorMsg') : translate('comments.saveComment.errorMsg');
      showErrorMessage(errorMsg);
    }
    setButtonDisabledWhenSave(false);
  };

  const addActions = (
    <Button onClick={() => saveCommentFunc()} className="btn-add" buttonStyle="primary" disabled={isSaveDisabled()}>
      {translate('common.add')}
    </Button>
  );

  const editActions = (
    <div className={classes.actionRow}>
      <Button onClick={onCancelHandler} buttonStyle="destructive" className={`btn-cancel ${classes.cancelBtn}`}>
        {translate('common.cancel')}
      </Button>
      <Button onClick={() => saveCommentFunc(comment.id)} className="btn-update" buttonStyle="primary" disabled={isSaveDisabled()}>
        {translate('common.update')}
      </Button>
    </div>
  );

  const handleChangeCallback = React.useCallback(input => {
    setInputValue(input);
  }, []);

  // const onSelectedFiles = files => {
  //   const selectedFiles = [...attachments];
  //   let filename;
  //   files.forEach(file => {
  //     filename = file.name.trim();
  //     selectedFiles.push({ filename });
  //   });
  //   setAttachments(selectedFiles);
  // };

  return (
    <div className={`${classes.root} ${editMode ? 'editMode' : ''}`}>
      {!editMode && !isMobile && <UserBadge user={currentUser} format={FIRST_LAST} badgeStyle={badgeStyle} noName />}
      <div className={`${classes.input} ${editMode || isMobile ? '' : classes.inputField}`}>
        <TextArea
          disabled={isAssessmentReadOnly}
          t={translate}
          value={inputValue}
          maxCharactersAllowed={commentMetadata?.text_max_characters_allowed}
          onChange={handleChangeCallback}
        />
        {/* <Attachments attachments={attachments} /> */}
        <div id="actions" className={classes.inputActions}>
          {/* <span className={classes.attachAction} onClick={() => {}} onKeyDown={() => {}} role="button" tabIndex={0}>
            <FileSelector onSelectedFiles={onSelectedFiles} style={{ display: 'none' }}>
              <Attachment className={classes.attachmentIcon} />
              {translate('comments.attach')}
            </FileSelector>
          </span> */}
          {!editMode && addActions}
          {editMode && editActions}
        </div>
      </div>
    </div>
  );
};

export const CommentInputHOC = compose(
  withRouter,
  withApollo,
  withTranslation(),
  graphql(getCurrentUser, { name: 'getCurrentUser' }),
  mapProps(props => ({
    editMode: props.editMode,
    comment: props.comment,
    onCancel: props.onCancel,
    translate: props.t,
    history: props.history,
    getCurrentUser: props.getCurrentUser.getCurrentUser,
    sourceFieldId: props.sourceFieldId,
    apolloClient: props.client,
    commentMetadata: props.commentMetadata,
    updateCommentsState: props.updateCommentsState,
    isAssessmentReadOnly: props.isAssessmentReadOnly
  }))
);

export const CommentInput = CommentInputHOC(CommentInputBase);
